/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Modal for creating a proposal for a requirement. Lets the user
 * choose a company profile (1, 2, or 3), enter description, delivery time, cost,
 * and attach a quotation file. Used on desktop when creating proposals from the
 * requirements view.
 * @module modals/ProposalCreateModal
 */

import React, { useRef, useEffect, useState, useMemo } from "react";
import styles from "./ProposalCreateModal.module.css";
import { useNavigate } from "react-router";
import Select from "react-select";
import { ReactComponent as SendIcon } from "../../assets/icons/ProposalDisplayModal/SendIcon.svg";
import FileUploadArea from "./FileUploadArea";
import { CircularProgress } from "@mui/material";
import {
  handleFileUpload,
  createProposal,
} from "../../logic/proposal/index.js";
import { useFieldValidation } from "../../hooks/useFieldValidation";
import { getUser } from "../../logic/user/index.js";
import { useTranslation } from "react-i18next";
import { selectStyles } from "../../utils/common.js";

/**
 * Modal for creating a proposal: profile selector, description, delivery time, cost, and quotation file.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function(): void} props.onClose - Called when the modal is closed
 * @param {Object} [props.requirement] - Requirement to create the proposal for (e.g. _id, titulo)
 * @param {function(): void} [props.onProposalCreated] - Called after a proposal is created successfully
 * @returns {JSX.Element}
 */
const ProposalCreateModal = ({
  open,
  onClose,
  requirement,
  onProposalCreated,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState(0);
  const [formData, setFormData] = React.useState({
    descripcion: "",
    tiempoDeEntrega: "",
    costo: "",
    cotizacion: null,
  });
  const [isDragging, setIsDragging] = React.useState(false);

  const profileOptions = useMemo(
    () => [
      { value: 0, label: t("pages.companyProfile.profile1") },
      { value: 1, label: t("pages.companyProfile.profile2") },
      { value: 2, label: t("pages.companyProfile.profile3") },
    ],
    [t],
  );
  const [error, setError] = React.useState("");
  const [isUploading, setIsUploading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    propuestaDescripcion: "",
    propuestaCosto: "",
    tiempoDeEntrega: "",
  });

  const MIN_DESCRIPTION_LENGTH = 250;
  const MAX_DESCRIPTION_LENGTH = 300;

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  /**
   * Updates a single form field in formData state.
   * @param {string} field - Field name (e.g. "descripcion", "tiempoDeEntrega", "costo", "cotizacion")
   * @param {string|File|null} value - New value for the field
   */
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Handles drag-over on the file upload area; prevents default and sets dragging state.
   * @param {DragEvent} e - Drag event
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handles drag-leave on the file upload area; clears dragging state.
   * @param {DragEvent} e - Drag event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handles file drop: validates type (PDF, JPG, PNG) and size (max 10MB), then uploads via handleFileUpload.
   * @param {DragEvent} e - Drag event containing the dropped file
   * @returns {Promise<void>}
   */
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        setError(t("errors.validation.ALLOWED_FILES_TYPES_1"));
        setFormData((prev) => ({
          ...prev,
          cotizacion: null,
        }));
        return;
      }

      if (file.size > maxSize) {
        setError(t("errors.validation.FILE_TOO_LARGE_1"));
        setFormData((prev) => ({
          ...prev,
          cotizacion: null,
        }));
        return;
      }

      setError("");
      await handleFileUpload(file, setFormData, setError, setIsUploading);
    }
  };

  /**
   * Handles file input change: validates type (PDF, JPG, PNG) and size (max 10MB), then uploads via handleFileUpload.
   * @param {Event} e - Change event from the file input
   * @returns {Promise<void>}
   */
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        setError(t("errors.validation.ALLOWED_FILES_TYPES_1"));
        setFormData((prev) => ({
          ...prev,
          cotizacion: null,
        }));
        return;
      }

      if (file.size > maxSize) {
        setError(t("errors.validation.FILE_TOO_LARGE_1"));
        setFormData((prev) => ({
          ...prev,
          cotizacion: null,
        }));
        return;
      }

      setError("");
      await handleFileUpload(file, setFormData, setError, setIsUploading);
    }
  };

  /**
   * Submits the proposal: fetches user RFC, calls createProposal with formData and selectedProfile, then onProposalCreated on success.
   * @param {Event} e - Form submit event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const userData = await getUser(localStorage.getItem("userId"), navigate);
    if (userData) {
      const companyRFC = userData.RFC;
      const success = await createProposal(
        formData,
        requirement,
        onClose,
        setError,
        setIsSubmitting,
        companyRFC,
        selectedProfile,
      );

      if (success && onProposalCreated) {
        onProposalCreated();
      }
    }
  };

  /**
   * Whether the form is valid and submit can be enabled: profile selected (0–2), description length 250–300,
   * delivery time and quotation filled, and no field validation errors.
   * @returns {boolean}
   */
  const isFormValid = () => {
    const descriptionLength = formData.descripcion.trim().length;
    const profileValid =
      selectedProfile !== undefined &&
      selectedProfile !== null &&
      [0, 1, 2].includes(Number(selectedProfile));
    return (
      profileValid &&
      descriptionLength >= MIN_DESCRIPTION_LENGTH &&
      descriptionLength <= MAX_DESCRIPTION_LENGTH &&
      formData.tiempoDeEntrega.trim() !== "" &&
      formData.cotizacion !== null &&
      !hasErrors
    );
  };

  /**
   * Returns the validation state for the description field based on length (neutral, valid, or error).
   * @returns {"neutral"|"valid"|"error"}
   */
  const getDescriptionValidationState = () => {
    const length = formData.descripcion.trim().length;
    if (length === 0) {
      return "neutral";
    }
    if (length < MIN_DESCRIPTION_LENGTH || length > MAX_DESCRIPTION_LENGTH) {
      return "error";
    }
    return "valid";
  };

  const descriptionValidationState = getDescriptionValidationState();

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={styles.modalOverlay}>
        <dialog ref={dialogRef} className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>
              {t("components.proposalCreateModal.title")}: {requirement?.titulo}
            </h2>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.modalContent}>
            <form onSubmit={handleSubmit} className={styles.proposalForm}>
              <div className={styles.profileSelectContainer}>
                <label className={styles.profileSelectLabel}>
                  {t("pages.companyProfile.availableProfiles")}
                </label>
                <Select
                  value={profileOptions.find(
                    (o) => o.value === selectedProfile,
                  )}
                  options={profileOptions}
                  onChange={(option) => setSelectedProfile(option.value)}
                  styles={{
                    ...selectStyles,
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  className="select__control"
                />
              </div>

              <div className={styles.detailSection}>
                <h3>{t("components.proposalCreateModal.descriptionLabel")}</h3>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleChange("descripcion", value);
                    handleFieldChange("propuestaDescripcion", value);
                  }}
                  className={`${styles.input} ${
                    descriptionValidationState === "error" ||
                    getFieldValidationState(
                      "propuestaDescripcion",
                      formData.descripcion,
                    ) === "error"
                      ? styles.inputError
                      : ""
                  }`}
                  placeholder={t(
                    "components.proposalCreateModal.descriptionPlaceholder",
                  )}
                  required
                  maxLength={300}
                />
                {getErrorMessage("propuestaDescripcion") && (
                  <p className={styles.errorMessage}>
                    {getErrorMessage("propuestaDescripcion")}
                  </p>
                )}
                <div className={styles.characterCounter}>
                  <span
                    className={`${styles.counterText} ${
                      descriptionValidationState === "error"
                        ? styles.counterError
                        : ""
                    }`}
                  >
                    {formData.descripcion.length}/{MAX_DESCRIPTION_LENGTH}
                  </span>
                  {formData.descripcion.length > 0 &&
                    formData.descripcion.length < MIN_DESCRIPTION_LENGTH && (
                      <span className={styles.minLengthHint}>
                        {t(
                          "components.proposalCreateModal.descriptionMinPart1",
                        )}{" "}
                        {MIN_DESCRIPTION_LENGTH}{" "}
                        {t(
                          "components.proposalCreateModal.descriptionMinPart2",
                        )}
                      </span>
                    )}
                </div>
              </div>

              <div className={styles.detailsRow}>
                <div className={styles.detailSection}>
                  <h3>
                    {t("components.proposalCreateModal.deliveryTimeLabel")}
                  </h3>
                  <input
                    type="text"
                    name="tiempoDeEntrega"
                    value={formData.tiempoDeEntrega}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange("tiempoDeEntrega", value);
                      handleFieldChange("tiempoDeEntrega", value);
                    }}
                    className={`${styles.input} ${
                      getFieldValidationState(
                        "tiempoDeEntrega",
                        formData.tiempoDeEntrega,
                      ) === "error"
                        ? styles.inputError
                        : ""
                    }`}
                    placeholder={t(
                      "components.proposalCreateModal.deliveryTimePlaceholder",
                    )}
                    required
                  />
                  {getErrorMessage("tiempoDeEntrega") && (
                    <p className={styles.errorMessage}>
                      {getErrorMessage("tiempoDeEntrega")}
                    </p>
                  )}
                </div>

                <div className={styles.detailSection}>
                  <h3>{t("components.proposalCreateModal.costLabel")}</h3>
                  <input
                    type="text"
                    name="costo"
                    value={formData.costo}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numbers and one decimal point
                      const filteredValue = value.replace(/[^\d.]/g, "");

                      handleChange("costo", filteredValue);
                      handleFieldChange("propuestaCosto", filteredValue);
                    }}
                    className={`${styles.input} ${
                      getFieldValidationState(
                        "propuestaCosto",
                        formData.costo,
                      ) === "error"
                        ? styles.inputError
                        : ""
                    }`}
                    placeholder={t(
                      "components.proposalCreateModal.costPlaceholder",
                    )}
                    required
                  />
                  {getErrorMessage("propuestaCosto") && (
                    <p className={styles.errorMessage}>
                      {getErrorMessage("propuestaCosto")}
                    </p>
                  )}
                  <p className={styles.optionalText}>
                    *{t("components.proposalCreateModal.optionalText")}
                  </p>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>{t("components.proposalCreateModal.quoteLabel")}</h3>
                <FileUploadArea
                  file={formData.cotizacion}
                  isUploading={isUploading}
                  isDragging={isDragging}
                  error={error}
                  onFileChange={handleFileChange}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById("fileInput").click()}
                />
                <input
                  id="fileInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t("components.proposalCreateModal.cancelButton")}
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" />
                      <span>{t("components.proposalCreateModal.sending")}</span>
                    </>
                  ) : (
                    <>
                      <SendIcon className={styles.sendIcon} />
                      {t("components.proposalCreateModal.sendButton")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </div>
    </>
  );
};

export default ProposalCreateModal;
